




















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { WaterTank, WaterTanksApi } from '@/api';
import InformationRow from '@/components/display/information-row.vue';
import Toast from '@/components/display/toast.vue';
import { PictorialStackedSeries, SlicedChart } from '@amcharts/amcharts5/percent';
import { color, Color, Root } from '@amcharts/amcharts5';

@Component({
	components: { InformationRow },
})
export default class WaterTankCurrentStatus extends Vue {
	@Prop({ required: true, type: Object })
	private waterTank!: WaterTank;

	@Watch('waterTank')
	public async onWaterTankChanged(): Promise<void> {
		this.currentState = await this.fetchCurrentState();
		this.chartSeries?.data.setAll(this.formatChartSeriesData(this.currentState?.percentFull ?? 0));
	}

	public currentState: any | null = null;
	public loading: boolean = true;

	public get currentVolumeString(): string {
		if (typeof this.currentState?.volume === 'number') {
			return `${this.currentState.volume} L - ${this.lastVolumeTime}`;
		}
		return 'Unknown';
	}

	public get lastVolumeTime(): string {
		if (this.currentState?.volumeTs) {
			return Vue.filter('dateTimeDiff')(this.currentState.volumeTs * 1000);
		}
		return 'Unknown';
	}

	private waterTanksApi: WaterTanksApi = new WaterTanksApi();
	private toast: Toast = new Toast();
	private chartSeries: PictorialStackedSeries | null = null;

	public async created(): Promise<void> {
		this.currentState = await this.fetchCurrentState();
		this.chartSeries = this.renderChart();
		setTimeout(() => (this.loading = false), 500);
	}

	private async fetchCurrentState(): Promise<object | null> {
		if (!this.waterTank?.id) return null;
		const currentStateResponse: any = await this.waterTanksApi.getWaterTankCurrentState(
			this.waterTank.id,
		);
		if (currentStateResponse?.errorMessage) {
			this.toast.makeToast(currentStateResponse.errorMessage, 'Error', 'danger');
			return null;
		}
		return currentStateResponse.data?.data ?? {};
	}

	private renderChart(): PictorialStackedSeries {
		const root = Root.new('waterTankVolume');
		const chart = root.container.children.push(
			SlicedChart.new(root, {
				paddingTop: 20,
				paddingBottom: 0,
				layout: root.verticalLayout,
			}),
		);
		const series = chart.series.push(
			PictorialStackedSeries.new(root, {
				orientation: 'vertical',
				valueField: 'value',
				categoryField: 'name',
				startLocation: 1,
				endLocation: 0,
				svgPath:
					'M 27.41 32.94 C 27.41 36.13 24.88 38.68 21.84 38.68 C 18.8 38.68 16.27 36.13 16.27 32.94 C 16.27 30.64 17.28 28.22 19.25 25.79 C 19.82 25.09 20.71 24.07 21.91 23.04 C 22.48 23.49 23.49 24.51 24.57 25.79 C 26.41 28.21 27.42 30.7 27.42 32.94 M 26.22 24.39 C 24.76 22.54 23.24 21.26 22.86 21.01 C 22.29 20.56 21.34 20.56 20.77 21.01 L 20.71 21.07 C 19.19 22.28 18.11 23.56 17.42 24.39 C 15.14 27.2 14 30.2 14 33.01 C 14 37.41 17.48 41.05 21.79 41.05 C 26.1 41.05 29.58 37.48 29.58 33.01 C 29.64 30.2 28.5 27.26 26.22 24.39 M 13.04 2.3 L 3.93 8.49 C 3.62984 8.69803 3.36853 8.94052 3.1495100000000003 9.21035 C 4.6955 9.41306 6.27275 9.59538 7.85 9.7 C 8.48 9.76 8.93 10.34 8.86 10.98 C 8.86 11.62 8.35 12.06 7.78 12.06 L 7.66 12.06 C 5.89331 11.88874 4.11849 11.71748 2.3502 11.45101 C 2.35007 11.46399 2.35 11.47699 2.35 11.49 L 2.35 44.62 C 2.35 46.22 3.43 47.68 5.01 48.13 C 16.09 51.26 27.74 51.26 38.82 48.13 C 40.4 47.68 41.48 46.28 41.48 44.62 C 41.48 44.61941 41.48 11.48882 41.48 11.48824 C 35.01499 12.34626 28.41768 12.78 21.83 12.78 L 21.84 12.77 C 18.74 12.77 15.64 12.64 12.53 12.45 C 11.9 12.39 11.39 11.88 11.45 11.24 C 11.51 10.6 12.02 10.09 12.65 10.16 C 21.98626 10.71319 31.444960000000002 10.43752 40.73359 9.25073 C 40.50412 8.95438 40.22481 8.69582 39.9 8.49 L 30.78 2.3 L 13.04 2.3 Z M 21.84 52.79 C 15.95 52.79 10.06 51.96 4.3 50.36 C 1.77 49.66 0 47.3 0 44.62 L 0 11.49 C 0 9.51 0.9500000000000001 7.66 2.6 6.57 L 12.03 0.19 C 12.22 0.06 12.47 0 12.66 0 L 31.02 0 C 31.27 0 31.46 0.06 31.65 0.19 L 41.08 6.57 C 42.73 7.65 43.68 9.51 43.68 11.48 L 43.68 38.31 L 45.21 38.31 C 45.72 38.31 46.1 38.69 46.1 39.2 L 46.1 42.9 C 46.1 43.41 45.72 43.79 45.21 43.79 L 43.68 43.79 L 43.68 44.61 C 43.68 47.29 41.91 49.65 39.37 50.35 C 33.61 51.95 27.72 52.78 21.83 52.78 L 21.84 52.79 Z M 44.32 42 L 44.32 40.09 L 43.68 40.09 L 43.68 42 L 44.32 42 Z',
				alignLabels: false,
			}),
		);
		series.slices.template.setAll({
			templateField: 'sliceSettings',
		});
		series.labels.template.set('forceHidden', true);
		series.data.setAll(this.formatChartSeriesData(this.currentState?.percentFull ?? 0));
		chart.appear();
		return series;
	}

	private formatChartSeriesData(
		percentFull: number,
	): Array<{
		name: string;
		sliceSettings: {
			fill: Color;
			stroke: Color;
			showTooltipOn?: 'hover' | 'always';
			tooltipText?: string;
		};
		value: number;
	}> {
		return [
			{
				name: 'Volume',
				value: percentFull,
				sliceSettings: {
					fill: color(0x0167b3),
					stroke: color(0x0167b3),
					showTooltipOn: 'always',
				},
			},
			{
				name: 'Empty',
				value: 100 - percentFull,
				sliceSettings: {
					fill: color(0xdddddd),
					stroke: color(0xdddddd),
					tooltipText: '',
				},
			},
		];
	}
}
